import React, { useState, useEffect, useMemo, useCallback } from "react";
import { PageHeader } from "../../components/layout/header/Header";
import { StandardPageWrapper } from "../../components/layout/StandardPageWrapper";
import { Pill } from "@truevault/aquilo";
import {
  ExclamationTriangleIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import { ChildOrganization } from "../../../common/service/server/controller/OrganizationVendorClientController";
import { Tabs, TabPanel, TabOptions } from "../../components/Tabs";
import "../../../../sass/widgets/cs-compliance-dashboard.scss";

type DashboardTab = "overview" | "automations";

const TAB_OPTIONS: TabOptions<DashboardTab> = [
  { label: "Overview", value: "overview" },
  { label: "Automations", value: "automations" },
];

interface DashboardSummary {
  totalOrganizations: number;
  organizationsWithIssues: number;
  organizationsWithCriticalIssues: number;
  averageIssuesPerOrg: number;
}

interface ApiComplianceData {
  organizationPublicId: string;
  organizationName: string;
  phase: string;
  onboardingOver30Days: boolean | null;
  privacyEmailDisconnected: boolean | null;
  overdueRequests: number | null;
  pendingTodos: number | null;
  dashboardQuestions: number | null;
  ninetyDaysLogin: boolean | null;
  fifteenDaysLogin: boolean | null;
  incompleteSteps: boolean | null;
  missingDesignatedRep: boolean | null;
  totalIssues: number | null;
  lastAuditDate: string;
  // New fields for onboarding table
  subdomainVerified: boolean | null;
  hrNotCompleted: boolean | null;
  incompletePrivacyCenter: string | null; // Text describing what's incomplete
  incompleteDsarWorkflows: string | null; // Text describing incomplete DSAR workflow sections
  childOrganizationsJson: string | null; // Cached child org data
  automationFailures: string | null; // ISO timestamp of last automation failure in recent DELETE/OPT_OUT requests
  disconnectedAutomations: string | null; // Comma-separated names of disconnected integrations (POL-7334)
  disconnectedAutomationsAt: string | null; // ISO timestamp of most recent disconnect observation
  // Row number for display
  rowNumber?: number;
}

interface DashboardResponse {
  summary: DashboardSummary;
  organizations: ApiComplianceData[];
}

// Custom expandable table component
const ExpandableTable = ({
  data,
  columns,
  keyExtractor,
  className,
  childOrgsMap,
  childOrgDataMap,
  expandedOrgs,
}: {
  data: ApiComplianceData[];
  columns: any[];
  keyExtractor: (item: ApiComplianceData) => string;
  className?: string;
  childOrgsMap: Record<string, ChildOrganization[]>;
  childOrgDataMap: Record<string, ApiComplianceData>;
  expandedOrgs: Set<string>;
}) => {
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const handleSort = (columnKey: string, sortable: boolean) => {
    if (!sortable) return;

    if (sortField === columnKey) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(columnKey);
      setSortDirection("asc");
    }
  };

  const sortedData = useMemo(() => {
    if (!sortField) return data;

    const column = columns.find((col) => col.key === sortField);
    if (!column || !column.sortable) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortField as keyof ApiComplianceData];
      const bValue = b[sortField as keyof ApiComplianceData];

      // Handle null values
      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return 1;
      if (bValue === null) return -1;

      let comparison = 0;

      if (column.sortType === "boolean") {
        comparison = (aValue ? 1 : 0) - (bValue ? 1 : 0);
      } else if (column.sortType === "number") {
        comparison = (aValue as number) - (bValue as number);
      } else {
        comparison = String(aValue).localeCompare(String(bValue));
      }

      return sortDirection === "desc" ? -comparison : comparison;
    });
  }, [data, sortField, sortDirection, columns]);

  const getSortIcon = (columnKey: string, sortable: boolean) => {
    if (!sortable) return null;

    if (sortField === columnKey) {
      return sortDirection === "asc" ? " ↑" : " ↓";
    }
    return "";
  };
  const renderChildRow = (childOrg: ChildOrganization) => {
    const childData = childOrgDataMap[childOrg.publicId];

    if (!childData) {
      // Fallback for child orgs without compliance data
      return (
        <tr
          key={`child-${childOrg.publicId}`}
          className="bg-gray-50"
          data-org-id={childOrg.publicId}
        >
          <td className="whitespace-nowrap text-sm px-3 py-2 text-gray-500 text-center">
            {/* Empty row number for child */}
          </td>
          <td
            className="whitespace-nowrap text-sm px-3 py-2 text-gray-500"
            colSpan={columns.length - 1}
          >
            <div className="flex items-center pl-8">
              <span className="text-gray-600 italic">↳ {childOrg.name}</span>
              <span className="ml-2 text-xs text-gray-400">(Child Organization - No Data)</span>
            </div>
          </td>
        </tr>
      );
    }

    // Render child with full compliance data but indented organization name
    return (
      <tr key={`child-${childOrg.publicId}`} className="bg-gray-50" data-org-id={childOrg.publicId}>
        {columns.map((column) => (
          <td
            key={column.key.toString()}
            className="whitespace-nowrap text-sm px-3 py-4 text-gray-500"
          >
            {column.key === "rowNumber" ? (
              // Empty row number for child
              <div className="text-center"></div>
            ) : column.key === "organizationName" ? (
              // Special rendering for child org name with indentation
              <div className="flex items-center pl-8">
                <span className="text-gray-600 italic">↳ {childData.organizationName}</span>
                <span className="ml-2 text-xs text-gray-400">(Child)</span>
              </div>
            ) : // Render other columns normally with child data
            column.render ? (
              column.render(childData)
            ) : (
              String(childData[column.key as keyof ApiComplianceData] ?? "")
            )}
          </td>
        ))}
      </tr>
    );
  };

  return (
    <div className={className}>
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((column) => (
              <th
                key={column.key.toString()}
                className={`px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                  column.sortable ? "cursor-pointer hover:bg-gray-100 select-none" : ""
                }`}
                onClick={() => handleSort(column.key, column.sortable)}
              >
                {column.header}
                {getSortIcon(column.key, column.sortable)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {sortedData.map((item) => {
            const rowId = keyExtractor(item);
            const childOrgs = childOrgsMap[item.organizationPublicId] || [];
            const isExpanded = expandedOrgs.has(item.organizationPublicId);

            return (
              <React.Fragment key={rowId}>
                {/* Parent row */}
                <tr data-org-id={item.organizationPublicId}>
                  {columns.map((column) => (
                    <td
                      key={column.key.toString()}
                      className="whitespace-nowrap text-sm px-3 py-4 text-gray-500"
                    >
                      {column.render
                        ? column.render(item)
                        : String(item[column.key as keyof ApiComplianceData] ?? "")}
                    </td>
                  ))}
                </tr>
                {/* Child rows (when expanded) */}
                {isExpanded && childOrgs.map((childOrg) => renderChildRow(childOrg))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export const CSComplianceDashboardPage = () => {
  const [dashboardData, setDashboardData] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedOrgs, setExpandedOrgs] = useState<Set<string>>(new Set());
  const [childOrgsMap, setChildOrgsMap] = useState<Record<string, ChildOrganization[]>>({});
  const [childOrgDataMap, setChildOrgDataMap] = useState<Record<string, ApiComplianceData>>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [tab, setTab] = useState<DashboardTab>("overview");
  // Remove searchNotFound and searchResults as we're filtering instead
  // const [searchNotFound, setSearchNotFound] = useState(false);
  // const [searchResults, setSearchResults] = useState<{
  //   parentMatches: ApiComplianceData[];
  //   childMatches: Array<{ child: ChildOrganization; parentId: string }>;
  // }>({ parentMatches: [], childMatches: [] });

  const toggleExpanded = useCallback((orgId: string) => {
    setExpandedOrgs((prev) => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(orgId)) {
        newExpanded.delete(orgId);
      } else {
        newExpanded.add(orgId);
      }
      return newExpanded;
    });
  }, []);

  // Remove scrollToOrganization, highlightElement, performSearch, handleSearchClick functions
  // as we're filtering instead of scrolling/highlighting

  // Remove the search effect that was calling performSearch
  // useEffect(() => {
  //   const timeoutId = setTimeout(() => {
  //     performSearch(searchTerm);
  //   }, 300);
  //
  //   return () => clearTimeout(timeoutId);
  // }, [searchTerm, performSearch]);

  const clearSearch = useCallback(() => {
    setSearchTerm("");
  }, []);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const response = await fetch("/admin/cs-compliance-dashboard/");
        if (!response.ok) {
          throw new Error(
            `Failed to fetch dashboard data: ${response.status} ${response.statusText}`,
          );
        }
        const data: DashboardResponse = await response.json();

        // Process cached child organization data instead of making API calls
        const newChildOrgsMap: Record<string, ChildOrganization[]> = {};
        const allChildOrgIds = new Set<string>();
        const childOrgDataMap: Record<string, ApiComplianceData> = {};

        data.organizations.forEach((org) => {
          // Parse cached child organization data
          if (org.childOrganizationsJson) {
            try {
              const childOrgs: ChildOrganization[] = JSON.parse(org.childOrganizationsJson);
              newChildOrgsMap[org.organizationPublicId] = childOrgs;
              childOrgs.forEach((child) => allChildOrgIds.add(child.publicId));
            } catch (error) {
              console.error(`Failed to parse child orgs for ${org.organizationName}:`, error);
              newChildOrgsMap[org.organizationPublicId] = [];
            }
          } else {
            newChildOrgsMap[org.organizationPublicId] = [];
          }
        });

        // Create a map of child org compliance data
        data.organizations.forEach((org) => {
          if (allChildOrgIds.has(org.organizationPublicId)) {
            childOrgDataMap[org.organizationPublicId] = org;
          }
        });

        // Filter out child organizations from main data
        const filteredOrganizations = data.organizations.filter(
          (org) => !allChildOrgIds.has(org.organizationPublicId),
        );

        setDashboardData({
          ...data,
          organizations: filteredOrganizations,
        });
        setChildOrgsMap(newChildOrgsMap);
        setChildOrgDataMap(childOrgDataMap);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const onboardingData = useMemo(() => {
    if (!dashboardData) return [];

    // Filter for GET_COMPLIANT phase organizations
    let filteredOrgs = dashboardData.organizations.filter((org) => org.phase === "GET_COMPLIANT");

    // Apply search filter if search term exists
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filteredOrgs = filteredOrgs.filter((org) => {
        // Check if parent org matches
        const parentMatches = org.organizationName.toLowerCase().includes(searchLower);

        // Check if any child org matches
        const childOrgs = childOrgsMap[org.organizationPublicId] || [];
        const childMatches = childOrgs.some((child) =>
          child.name.toLowerCase().includes(searchLower),
        );

        return parentMatches || childMatches;
      });
    }

    // Sort by onboardingOver30Days (true first for issues), then by organization name
    const sortedOrgs = [...filteredOrgs].sort((a, b) => {
      // Sort by onboardingOver30Days - true (issues) should come first
      if (a.onboardingOver30Days !== b.onboardingOver30Days) {
        if (a.onboardingOver30Days === true) return -1;
        if (b.onboardingOver30Days === true) return 1;
        if (a.onboardingOver30Days === null) return 1;
        if (b.onboardingOver30Days === null) return -1;
      }

      // Then sort by organization name alphabetically
      return a.organizationName.localeCompare(b.organizationName);
    });

    // Add row numbers based on the sorted order
    return sortedOrgs.map((org, index) => ({
      ...org,
      rowNumber: index + 1,
    }));
  }, [dashboardData, childOrgsMap, searchTerm]);

  const postOnboardingData = useMemo(() => {
    if (!dashboardData) return [];

    // Filter for non-GET_COMPLIANT phase organizations
    let filteredOrgs = dashboardData.organizations.filter((org) => org.phase !== "GET_COMPLIANT");

    // Apply search filter if search term exists
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filteredOrgs = filteredOrgs.filter((org) => {
        // Check if parent org matches
        const parentMatches = org.organizationName.toLowerCase().includes(searchLower);

        // Check if any child org matches
        const childOrgs = childOrgsMap[org.organizationPublicId] || [];
        const childMatches = childOrgs.some((child) =>
          child.name.toLowerCase().includes(searchLower),
        );

        return parentMatches || childMatches;
      });
    }

    // Sort by totalIssues (descending - most issues first), then by organization name
    const sortedOrgs = [...filteredOrgs].sort((a, b) => {
      // Sort by totalIssues - higher issues should come first
      const aIssues = a.totalIssues ?? 0;
      const bIssues = b.totalIssues ?? 0;

      if (aIssues !== bIssues) {
        return bIssues - aIssues; // Descending order
      }

      // Then sort by organization name alphabetically
      return a.organizationName.localeCompare(b.organizationName);
    });

    // Add row numbers and sorting fields based on the sorted order
    return sortedOrgs.map((org, index) => ({
      ...org,
      rowNumber: index + 1,
      // Treat null dashboard questions as -2 for sorting
      dashboardQuestionsForSorting: org.dashboardQuestions === null ? -2 : org.dashboardQuestions,
    }));
  }, [dashboardData, childOrgsMap, searchTerm]);

  // Recent Disconnected Orgs: every org (parent or child) whose nightly heatlamp run flagged at least one
  // disconnected integration. The audit log mirrors the HAS_INVALID_INTEGRATION_CREDENTIAL_SLUG survey answer
  // (see POL-7278/POL-7334); disconnectedAutomationsAt is the most recent observation.
  const disconnectedOrgsData = useMemo(() => {
    if (!dashboardData) return [];

    // Include all rows (parents + children) so a child org with a disconnect surfaces here even if its parent
    // is healthy. The Automations tab is intentionally a flat list — child indentation doesn't add value here.
    let filteredOrgs = dashboardData.organizations
      .concat(Object.values(childOrgDataMap))
      .filter(
        (org) =>
          org.disconnectedAutomations !== null && org.disconnectedAutomations.trim().length > 0,
      );

    // Dedupe by orgId — child rows can also appear in the parent list when child === parent for non-grouped orgs.
    const seen = new Set<string>();
    filteredOrgs = filteredOrgs.filter((org) => {
      if (seen.has(org.organizationPublicId)) return false;
      seen.add(org.organizationPublicId);
      return true;
    });

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filteredOrgs = filteredOrgs.filter(
        (org) =>
          org.organizationName.toLowerCase().includes(searchLower) ||
          (org.disconnectedAutomations ?? "").toLowerCase().includes(searchLower),
      );
    }

    const sortedOrgs = [...filteredOrgs].sort((a, b) => {
      // Most recent disconnect first; nulls sort last (shouldn't happen given the filter above).
      const aAt = a.disconnectedAutomationsAt ?? "";
      const bAt = b.disconnectedAutomationsAt ?? "";
      if (aAt === bAt) return a.organizationName.localeCompare(b.organizationName);
      return bAt.localeCompare(aAt);
    });

    return sortedOrgs.map((org, index) => ({ ...org, rowNumber: index + 1 }));
  }, [dashboardData, childOrgDataMap, searchTerm]);

  // Auto-expand parent organizations when child organizations match search
  useEffect(() => {
    if (!searchTerm.trim() || !dashboardData) {
      // If no search term, don't auto-expand anything
      return;
    }

    const searchLower = searchTerm.toLowerCase();
    const parentsToExpand = new Set<string>();

    // Check all organizations to see if any children match
    const allOrgs = [...onboardingData, ...postOnboardingData];
    allOrgs.forEach((org) => {
      const childOrgs = childOrgsMap[org.organizationPublicId] || [];
      const hasMatchingChild = childOrgs.some((child) =>
        child.name.toLowerCase().includes(searchLower),
      );

      if (hasMatchingChild) {
        parentsToExpand.add(org.organizationPublicId);
      }
    });

    // Update expanded orgs to include parents with matching children
    setExpandedOrgs((prevExpanded) => {
      const newExpanded = new Set(prevExpanded);
      parentsToExpand.forEach((parentId) => {
        newExpanded.add(parentId);
      });
      return newExpanded;
    });
  }, [searchTerm, onboardingData, postOnboardingData, childOrgsMap, dashboardData]);

  const onboardingColumns = [
    {
      key: "rowNumber",
      header: "#",
      render: (item: ApiComplianceData) => (
        <div className="text-center font-medium">{item.rowNumber}</div>
      ),
      sortable: false,
    },
    {
      key: "organizationName",
      header: "Organization",
      render: (item: ApiComplianceData) => {
        const childOrgs = childOrgsMap[item.organizationPublicId] || [];
        const hasChildren = childOrgs.length > 0;
        const isExpanded = expandedOrgs.has(item.organizationPublicId);

        const handleToggle = () => toggleExpanded(item.organizationPublicId);

        return (
          <div className="compliance-org-name flex items-center">
            {hasChildren ? (
              <button
                onClick={handleToggle}
                className="mr-2 p-1 hover:bg-gray-100 rounded"
                aria-label={isExpanded ? "Collapse" : "Expand"}
              >
                {isExpanded ? (
                  <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4 text-gray-500" />
                )}
              </button>
            ) : (
              <div className="w-6" /> /* Spacer for alignment */
            )}
            <span>{item.organizationName}</span>
            {hasChildren && (
              <span className="ml-2 text-xs text-gray-500">({childOrgs.length})</span>
            )}
          </div>
        );
      },
      sortable: true,
      sortType: "string" as const,
    },
    {
      key: "onboardingOver30Days",
      header: "Onboarding 30+ Days",
      render: (item: ApiComplianceData) => {
        if (item.onboardingOver30Days === null) {
          return <span className="compliance-no-data">—</span>;
        }
        return item.onboardingOver30Days ? (
          <ExclamationTriangleIcon className="compliance-warning-icon" />
        ) : (
          <span className="compliance-no-issue">---</span>
        );
      },
      sortable: true,
      sortType: "boolean" as const,
    },
    {
      key: "overdueRequests",
      header: "Overdue Requests",
      render: (item: ApiComplianceData) => {
        if (item.overdueRequests === null) {
          return <span className="compliance-no-data">—</span>;
        }

        const getClassName = (count: number) => {
          if (count === 0) return "compliance-overdue-requests overdue-zero";
          if (count < 10) return "compliance-overdue-requests overdue-medium";
          return "compliance-overdue-requests overdue-high";
        };

        return item.overdueRequests > 0 ? (
          <div className={getClassName(item.overdueRequests)}>{item.overdueRequests}</div>
        ) : (
          <div className={getClassName(item.overdueRequests)}>0</div>
        );
      },
      sortable: true,
      sortType: "number" as const,
    },
    {
      key: "privacyEmailDisconnected",
      header: "Email Disconnected",
      render: (item: ApiComplianceData) => {
        if (item.privacyEmailDisconnected === null) {
          return <span className="compliance-no-data">—</span>;
        }
        return item.privacyEmailDisconnected ? (
          <ExclamationTriangleIcon className="compliance-warning-icon" />
        ) : (
          <span className="compliance-no-issue">---</span>
        );
      },
      sortable: true,
      sortType: "boolean" as const,
    },
    {
      key: "subdomainVerified",
      header: "Subdomain Verified",
      render: (item: ApiComplianceData) => {
        if (item.subdomainVerified === null) {
          return <span className="compliance-no-data">—</span>;
        }
        return item.subdomainVerified ? (
          <span className="compliance-no-issue">✓</span>
        ) : (
          <ExclamationTriangleIcon className="compliance-warning-icon" />
        );
      },
      sortable: true,
      sortType: "boolean" as const,
    },
    {
      key: "lastLogin15Days",
      header: "Last Login 15+ Days",
      render: (item: ApiComplianceData) => {
        if (item.fifteenDaysLogin === null) {
          return <span className="compliance-no-data">—</span>;
        }
        return item.fifteenDaysLogin ? (
          <ExclamationTriangleIcon className="compliance-warning-icon" />
        ) : (
          <span className="compliance-no-issue">---</span>
        );
      },
      sortable: true,
      sortType: "boolean" as const,
    },
    {
      key: "hrNotCompleted",
      header: "HR Not Completed",
      render: (item: ApiComplianceData) => {
        if (item.hrNotCompleted === null) {
          return <span className="compliance-no-data">—</span>;
        }
        return item.hrNotCompleted ? (
          <ExclamationTriangleIcon className="compliance-warning-icon" />
        ) : (
          <span className="compliance-no-issue">---</span>
        );
      },
      sortable: true,
      sortType: "boolean" as const,
    },
    {
      key: "incompletePrivacyCenter",
      header: "Incomplete Privacy Center",
      render: (item: ApiComplianceData) => {
        if (item.incompletePrivacyCenter === null || item.incompletePrivacyCenter === "") {
          return <span className="compliance-no-issue">---</span>;
        }
        return (
          <div className="compliance-incomplete-sections">
            <span className="text-sm">{item.incompletePrivacyCenter}</span>
          </div>
        );
      },
      sortable: true,
      sortType: "string" as const,
    },
  ];

  const postOnboardingColumns = [
    {
      key: "rowNumber",
      header: "#",
      render: (item: ApiComplianceData) => (
        <div className="text-center font-medium">{item.rowNumber}</div>
      ),
      sortable: false,
    },
    {
      key: "organizationName",
      header: "Organization",
      render: (item: ApiComplianceData) => {
        const childOrgs = childOrgsMap[item.organizationPublicId] || [];
        const hasChildren = childOrgs.length > 0;
        const isExpanded = expandedOrgs.has(item.organizationPublicId);

        const handleToggle = () => toggleExpanded(item.organizationPublicId);

        return (
          <div className="compliance-org-name flex items-center">
            {hasChildren ? (
              <button
                onClick={handleToggle}
                className="mr-2 p-1 hover:bg-gray-100 rounded"
                aria-label={isExpanded ? "Collapse" : "Expand"}
              >
                {isExpanded ? (
                  <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4 text-gray-500" />
                )}
              </button>
            ) : (
              <div className="w-6" /> /* Spacer for alignment */
            )}
            <span>{item.organizationName}</span>
            {hasChildren && (
              <span className="ml-2 text-xs text-gray-500">({childOrgs.length})</span>
            )}
          </div>
        );
      },
      sortable: true,
      sortType: "string" as const,
    },
    {
      key: "privacyEmailDisconnected",
      header: "Privacy Email Disconnected",
      render: (item: ApiComplianceData) => {
        if (item.privacyEmailDisconnected === null) {
          return <span className="compliance-no-data">—</span>;
        }
        return item.privacyEmailDisconnected ? (
          <ExclamationTriangleIcon className="compliance-warning-icon" />
        ) : (
          <span className="compliance-no-issue">---</span>
        );
      },
      sortable: true,
      sortType: "boolean" as const,
    },
    {
      key: "subdomainVerified",
      header: "Subdomain Verified",
      render: (item: ApiComplianceData) => {
        if (item.subdomainVerified === null) {
          return <span className="compliance-no-data">—</span>;
        }
        return item.subdomainVerified ? (
          <span className="compliance-no-issue">✓</span>
        ) : (
          <ExclamationTriangleIcon className="compliance-warning-icon" />
        );
      },
      sortable: true,
      sortType: "boolean" as const,
    },
    {
      key: "automationFailures",
      header: "Last Automation Failure",
      render: (item: ApiComplianceData) => {
        if (item.automationFailures === null) {
          return <span className="compliance-no-data">—</span>;
        }
        // Format the timestamp to a readable date
        const failureDate = new Date(item.automationFailures);
        const formattedDate = failureDate.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
        const formattedTime = failureDate.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
        return (
          <div className="compliance-automation-failure">
            <ExclamationTriangleIcon className="compliance-warning-icon" />
            <div className="failure-timestamp">
              <div className="failure-date">{formattedDate}</div>
              <div className="failure-time">{formattedTime}</div>
            </div>
          </div>
        );
      },
      sortable: true,
      sortType: "string" as const,
    },
    {
      key: "overdueRequests",
      header: "Overdue Requests",
      render: (item: ApiComplianceData) => {
        if (item.overdueRequests === null) {
          return <span className="compliance-no-data">—</span>;
        }

        const getClassName = (count: number) => {
          if (count === 0) return "compliance-overdue-requests overdue-zero";
          if (count < 10) return "compliance-overdue-requests overdue-medium";
          return "compliance-overdue-requests overdue-high";
        };

        return item.overdueRequests > 0 ? (
          <div className={getClassName(item.overdueRequests)}>{item.overdueRequests}</div>
        ) : (
          <div className={getClassName(item.overdueRequests)}>0</div>
        );
      },
      sortable: true,
      sortType: "number" as const,
    },
    {
      key: "pendingTodos",
      header: "Pending To-Do's",
      render: (item: ApiComplianceData) => {
        if (item.pendingTodos === null) {
          return <span className="compliance-no-data">—</span>;
        }
        return item.pendingTodos > 0 ? (
          <div className="compliance-pill-container">
            <div className="tw-boundary">
              <Pill variant="warning">{item.pendingTodos}</Pill>
            </div>
          </div>
        ) : (
          <span className="compliance-no-issue">---</span>
        );
      },
      sortable: true,
      sortType: "number" as const,
    },
    {
      key: "dashboardQuestions",
      header: "Unanswered Dashboard Questions",
      render: (item: ApiComplianceData) => {
        if (item.dashboardQuestions === null) {
          return <span className="compliance-no-data">—</span>;
        }

        const getClassName = (count: number) => {
          if (count === 0) return "compliance-dashboard-questions questions-zero";
          if (count < 3) return "compliance-dashboard-questions questions-medium";
          return "compliance-dashboard-questions questions-high";
        };

        return item.dashboardQuestions > 0 ? (
          <div className={getClassName(item.dashboardQuestions)}>{item.dashboardQuestions}</div>
        ) : (
          <div className={getClassName(item.dashboardQuestions)}>0</div>
        );
      },
      sortable: true,
      sortType: "number" as const,
      sortKey: "dashboardQuestionsForSorting", // Custom sort key
    },
    {
      key: "ninetyDaysLogin",
      header: "90+ Days Login",
      render: (item: ApiComplianceData) => {
        if (item.ninetyDaysLogin === null) {
          return <span className="compliance-no-data">—</span>;
        }
        return item.ninetyDaysLogin ? (
          <ExclamationTriangleIcon className="compliance-warning-icon" />
        ) : (
          <span className="compliance-no-issue">---</span>
        );
      },
      sortable: true,
      sortType: "boolean" as const,
    },
    {
      key: "incompleteSteps",
      header: "Incomplete Steps",
      render: (item: ApiComplianceData) => {
        if (item.incompleteSteps === null) {
          return <span className="compliance-no-data">—</span>;
        }
        return item.incompleteSteps ? (
          <ExclamationTriangleIcon className="compliance-warning-icon" />
        ) : (
          <span className="compliance-no-issue">---</span>
        );
      },
      sortable: true,
      sortType: "boolean" as const,
    },
    {
      key: "incompleteDsarWorkflows",
      header: "Incomplete DSAR Workflows",
      render: (item: ApiComplianceData) => {
        if (item.incompleteDsarWorkflows === null || item.incompleteDsarWorkflows === "") {
          return <span className="compliance-no-issue">---</span>;
        }
        return (
          <div className="compliance-incomplete-sections">
            <span className="text-sm">{item.incompleteDsarWorkflows}</span>
          </div>
        );
      },
      sortable: true,
      sortType: "string" as const,
    },
    {
      key: "missingDesignatedRep",
      header: "Missing Designated Rep",
      render: (item: ApiComplianceData) => {
        if (item.missingDesignatedRep === null) {
          return <span className="compliance-no-data">—</span>;
        }
        return item.missingDesignatedRep ? (
          <ExclamationTriangleIcon className="compliance-warning-icon" />
        ) : (
          <span className="compliance-no-issue">---</span>
        );
      },
      sortable: true,
      sortType: "boolean" as const,
    },
    {
      key: "totalIssues",
      header: "Total Issues",
      render: (item: ApiComplianceData) => {
        if (item.totalIssues === null) {
          return <span className="compliance-no-data">—</span>;
        }

        const getClassName = (count: number) => {
          if (count === 0) return "compliance-total-issues total-zero";
          if (count <= 3) return "compliance-total-issues total-low";
          return "compliance-total-issues total-high";
        };

        return <div className={getClassName(item.totalIssues)}>{item.totalIssues}</div>;
      },
      sortable: true,
      sortType: "number" as const,
    },
  ];

  const disconnectedColumns = [
    {
      key: "rowNumber",
      header: "#",
      render: (item: ApiComplianceData) => (
        <div className="text-center font-medium">{item.rowNumber}</div>
      ),
      sortable: false,
    },
    {
      key: "organizationName",
      header: "Organization",
      render: (item: ApiComplianceData) => (
        <div className="compliance-org-name">{item.organizationName}</div>
      ),
      sortable: true,
      sortType: "string" as const,
    },
    {
      key: "disconnectedAutomations",
      header: "Disconnected Integrations",
      render: (item: ApiComplianceData) => (
        <div className="compliance-disconnected-integrations">
          <ExclamationTriangleIcon className="compliance-warning-icon" />
          <span className="text-sm">{item.disconnectedAutomations}</span>
        </div>
      ),
      sortable: true,
      sortType: "string" as const,
    },
    {
      key: "disconnectedAutomationsAt",
      header: "Most Recent Issue",
      render: (item: ApiComplianceData) => {
        if (!item.disconnectedAutomationsAt) {
          return <span className="compliance-no-data">—</span>;
        }
        const observedAt = new Date(item.disconnectedAutomationsAt);
        const formattedDate = observedAt.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
        const formattedTime = observedAt.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
        return (
          <div className="compliance-automation-failure">
            <div className="failure-timestamp">
              <div className="failure-date">{formattedDate}</div>
              <div className="failure-time">{formattedTime}</div>
            </div>
          </div>
        );
      },
      sortable: true,
      sortType: "string" as const,
    },
  ];

  if (loading) {
    return (
      <StandardPageWrapper fullWidth>
        <PageHeader titleContent="CS Compliance Dashboard" />
        <div className="cs-compliance-dashboard">
          <div className="flex justify-center items-center h-64">
            <div className="text-lg">Loading dashboard data...</div>
          </div>
        </div>
      </StandardPageWrapper>
    );
  }

  if (error) {
    return (
      <StandardPageWrapper fullWidth>
        <PageHeader titleContent="CS Compliance Dashboard" />
        <div className="cs-compliance-dashboard">
          <div className="flex justify-center items-center h-64">
            <div className="text-lg text-red-600">Error loading dashboard: {error}</div>
          </div>
        </div>
      </StandardPageWrapper>
    );
  }

  return (
    <StandardPageWrapper fullWidth>
      <PageHeader titleContent="CS Compliance Dashboard" />

      <div className="cs-compliance-dashboard">
        <div className="tw-boundary">
          <h2 className="text-xl font-semibold mb-6">Customer Success Compliance Dashboard</h2>
        </div>

        {/* Tabs sit outside tw-boundary because Tailwind's preflight strips Material UI's Tab/button padding,
            collapsing the tabs to bare text (POL-7334). */}
        <Tabs
          options={TAB_OPTIONS}
          tab={tab}
          setTab={(t) => setTab(t as DashboardTab)}
          textColor="primary"
          ariaLabel="CS dashboard tabs"
        />

        {/* Search Bar — shared across tabs so search state survives tab switches. The Tailwind preset uses
            `important: ".tw-boundary"`, which scopes utilities to *descendants* of tw-boundary — applying
            margin classes (mt-*, mb-*) to the same element as `tw-boundary` silently no-ops, so we need an
            inner div to carry the spacing. */}
        <div className="tw-boundary">
          <div className="mb-6 mt-10">
            <div className="relative max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder={
                  tab === "automations"
                    ? "Filter by org or integration name..."
                    : "Filter organizations (including child orgs)..."
                }
                className="block w-full pl-8 pr-8 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className="absolute inset-y-0 right-0 flex items-center">
                {searchTerm && (
                  <button
                    type="button"
                    className="mr-2 h-5 w-5 text-gray-400 hover:text-gray-600 focus:outline-none"
                    onClick={clearSearch}
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {tab === "overview" &&
              searchTerm.trim() &&
              (onboardingData.length > 0 || postOnboardingData.length > 0) && (
                <div className="mt-2 text-sm text-green-600">
                  Showing {onboardingData.length + postOnboardingData.length} organization(s)
                  matching "{searchTerm}"
                </div>
              )}

            {tab === "overview" &&
              searchTerm.trim() &&
              onboardingData.length === 0 &&
              postOnboardingData.length === 0 && (
                <div className="mt-2 text-sm text-red-600">
                  No organizations found matching "{searchTerm}"
                </div>
              )}
          </div>
        </div>

        <TabPanel value="overview" tab={tab} className="tw-boundary">
          {/* Onboarding Organizations */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4">
              Onboarding Organizations ({onboardingData.length})
            </h3>
            <div className="compliance-table-container">
              <div className="tw-boundary">
                <ExpandableTable
                  data={onboardingData}
                  columns={onboardingColumns}
                  keyExtractor={(item) => item.organizationPublicId}
                  className="compliance-table w-full"
                  childOrgsMap={childOrgsMap}
                  childOrgDataMap={childOrgDataMap}
                  expandedOrgs={expandedOrgs}
                />
              </div>
            </div>
          </div>

          {/* Post-Onboarding Organizations */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4">
              Post-Onboarding Organizations ({postOnboardingData.length})
            </h3>
            <div className="compliance-table-container">
              <div className="tw-boundary">
                <ExpandableTable
                  data={postOnboardingData}
                  columns={postOnboardingColumns}
                  keyExtractor={(item) => item.organizationPublicId}
                  className="compliance-table w-full"
                  childOrgsMap={childOrgsMap}
                  childOrgDataMap={childOrgDataMap}
                  expandedOrgs={expandedOrgs}
                />
              </div>
            </div>
          </div>
        </TabPanel>

        <TabPanel value="automations" tab={tab} className="tw-boundary">
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4">
              Recent Disconnected Orgs ({disconnectedOrgsData.length})
            </h3>
            {disconnectedOrgsData.length === 0 ? (
              <div className="text-sm text-gray-500">
                {searchTerm.trim()
                  ? `No organizations with disconnected integrations match "${searchTerm}".`
                  : "No organizations have disconnected integrations as of the last nightly audit."}
              </div>
            ) : (
              <div className="compliance-table-container">
                <div className="tw-boundary">
                  <ExpandableTable
                    data={disconnectedOrgsData}
                    columns={disconnectedColumns}
                    keyExtractor={(item) => item.organizationPublicId}
                    className="compliance-table w-full"
                    childOrgsMap={{}}
                    childOrgDataMap={{}}
                    expandedOrgs={new Set()}
                  />
                </div>
              </div>
            )}
          </div>
        </TabPanel>
      </div>
    </StandardPageWrapper>
  );
};
